// ============================================
// SERMORELIN/METABOLIC SUPPORT QUESTIONNAIRE DATA
// ============================================
// Based on Beluga Health - Metabolic Support Questionnaire
// Medications: Sermorelin, Tesamorelin, MIC (Lipotrophics)
// Visit Type: metabolicSupport
// Follow-Up Frequency: Every 6 months

// ============================================
// DISQUALIFYING CONDITIONS
// ============================================
export const SERMORELIN_DISQUALIFYING_CONDITIONS = [
  'Any active or history of cancer'
];

// Health goals options
export const healthGoalsOptions = [
  { label: 'Maintain a healthy weight', value: 'maintain-weight' },
  { label: 'Improve metabolic health', value: 'improve-metabolic-health' },
  { label: 'Improve cardiovascular health', value: 'improve-cardiovascular-health' },
  { label: 'Improve overall quality of life', value: 'improve-quality-life' },
  { label: 'Improve mood', value: 'improve-mood' },
  { label: 'Increase energy', value: 'increase-energy' },
  { label: 'Improve muscle tone', value: 'improve-muscle-tone' },
  { label: 'Other', value: 'other' }
];

// Rating scale options
export const ratingScaleOptions = [
  { label: '1 (Poor)', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
  { label: '5 (Excellent)', value: '5' }
];

// Medical history conditions
export const medicalHistoryOptions = [
  { label: 'None of these apply to me', value: 'none' },
  { label: 'Any active or history of cancer', value: 'cancer', disqualifies: true },
  { label: 'Hypothyroidism/Underactive Thyroid', value: 'hypothyroidism' }
];

// Medications that may interact - ALL are disqualifying except "None".
// COX inhibitors are intentionally NOT in the intake list — Drive
// "Metabolic Support Questionnaire" (1Fl3MI5wnIw4QU3JRDSEL01ziK4oK_Z6CqxNWFYctWZU)
// only screens for muscarinic / somatostatin / propylthiouracil at intake.
// COX is screened in the follow-up flow (see metabolicFollowUpData.js).
export const interactingMedicationsOptions = [
  { label: 'Muscarinic antagonist (atropine)', value: 'muscarinic-antagonist', disqualifies: true },
  { label: 'Somatostatin-containing medications (clonidine and levodopa)', value: 'somatostatin', disqualifies: true },
  { label: 'Propylthiouracil', value: 'propylthiouracil', disqualifies: true },
  { label: 'None of the above', value: 'none' }
];

// ============================================
// SERMORELIN QUESTIONS
// ============================================

const sexAtBirthQuestion = {
  id: 'sex-at-birth',
  type: 'single-select',
  question: 'What was your sex assigned at birth?',
  options: [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' }
  ]
};

const pregnancyStatusQuestion = {
  id: 'pregnancy-status',
  type: 'single-select',
  question: 'Are you currently pregnant, breastfeeding or planning to become pregnant?',
  options: [
    { label: 'Yes', value: 'yes', disqualifies: true },
    { label: 'No', value: 'no' }
  ]
};

const pregnancyConsentQuestion = {
  id: 'pregnancy-consent',
  type: 'consent-dropdown',
  question: 'Consent (Pregnancy Risk)',
  consentText: `I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.

I agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.

I understand that I should stop taking this medication before attempting to become pregnant. I agree to consult with my healthcare provider prior to discontinuing the medication and before planning a pregnancy to ensure my safety and well-being.

By selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.`,
  options: [
    { label: 'I have read and understand the above information, I understand the risks and wish to proceed', value: 'consent' },
    { label: 'I have read the information and do NOT wish to proceed', value: 'decline', disqualifies: true }
  ]
};

const heightWeightQuestion = {
  id: 'height-weight',
  type: 'height-weight-bmi',
  question: 'What is your height and weight?',
  subtitle: 'This information helps us personalize your treatment',
  skipBmiCheck: true // Skip BMI calculation and disqualification for Sermorelin
};

const medicalConditionsQuestion = {
  id: 'medical-conditions',
  type: 'textarea',
  question: 'Please identify all your current medical conditions',
  subtitle: 'Include chronic conditions, past surgeries, and any other relevant health issues',
  placeholder: 'e.g., Hypertension, Asthma, Previous knee surgery, etc...',
  required: true
};

const medicationsListQuestion = {
  id: 'medications-list',
  type: 'textarea',
  question: 'Please list all your current medications including dosages',
  subtitle: 'Include prescription medications, over-the-counter drugs, and supplements',
  placeholder: 'e.g., Metformin 500mg twice daily, Lisinopril 10mg daily, Vitamin D 2000 IU daily...',
  required: true
};

const allergiesQuestion = {
  id: 'allergies',
  type: 'textarea',
  question: 'Please list all of your known allergies',
  subtitle: 'Include drug allergies, food allergies, and environmental allergies',
  placeholder: 'e.g., Penicillin (causes rash), Shellfish (anaphylaxis), Seasonal allergies...',
  required: true
};

const healthGoalsQuestion = {
  id: 'health-goals',
  type: 'multi-select',
  question: 'We would like to know your goals:',
  subtitle: 'Select all that apply',
  options: healthGoalsOptions
};

const healthGoalsOtherQuestion = {
  id: 'health-goals-other',
  type: 'textarea',
  question: 'Tell us about your goal with therapy',
  subtitle: 'Please describe your specific goals',
  placeholder: 'Describe what you hope to achieve with this treatment...'
};

const cardiovascularRatingQuestion = {
  id: 'cardiovascular-rating',
  type: 'single-select',
  question: 'On a scale of 1-5, how would you rate your average cardiovascular health?',
  options: ratingScaleOptions
};

const strengthRatingQuestion = {
  id: 'strength-rating',
  type: 'single-select',
  question: 'On a scale of 1-5, how would you rate your strength?',
  options: ratingScaleOptions
};

const medicalHistoryQuestion = {
  id: 'medical-history',
  type: 'multi-select',
  question: 'Do you currently have, or have you ever been diagnosed with, any of the following conditions?',
  subtitle: 'Select all that apply',
  options: medicalHistoryOptions
};

const thyroidConsentQuestion = {
  id: 'thyroid-consent',
  type: 'consent-dropdown',
  question: 'Important Information About Thyroid Function',
  consentText: `Sermorelin and tesamorelin can affect thyroid function by influencing how the body produces and converts thyroid hormones. If you have hypothyroidism or take thyroid medication, these treatments may alter your hormone levels or require adjustments to your thyroid dose. Monitoring thyroid function during therapy is recommended. Please report any changes in energy, mood, or temperature tolerance to your provider, as these may indicate a change in thyroid balance.`,
  options: [
    { label: 'I wish to proceed and will discuss this medication with my doctor', value: 'consent' },
    { label: 'I have read the consent and I do NOT wish to move forward', value: 'decline', disqualifies: true }
  ]
};

const currentMedicationsQuestion = {
  id: 'current-medications',
  type: 'multi-select',
  question: 'Do you take any of the following medications?',
  subtitle: 'Select all that apply',
  options: interactingMedicationsOptions
};

const additionalQuestionsQuestion = {
  id: 'additional-questions',
  type: 'textarea',
  question: 'What other information or questions do you have for the doctor?',
  subtitle: 'Feel free to share any concerns or questions',
  placeholder: 'Type your questions or concerns here...'
};

const basicContactQuestion = {
  id: 'basic-contact',
  type: 'basic-contact',
  question: 'How can we reach you?',
  subtitle: "We'll use this to send updates about your treatment"
};

const mainConsentQuestion = {
  id: 'main-consent',
  type: 'consent-dropdown',
  question: 'Consent for Sermorelin/Tesamorelin Therapy',
  consentText: `Medications such as sermorelin and tesamorelin are medications that may be prescribed to support your metabolism, though it is not FDA-approved for this specific use. Its application for metabolism support is considered off-label, meaning it is prescribed by your healthcare provider despite not being specifically approved by the FDA for this purpose.

The purpose of this consent form is to ensure that you understand the following:
• The reasons for using sermorelin or tesamorelin
• The potential benefits and risks
• The monitoring plan
• Your responsibilities during treatment

Please read this document carefully.

Sermorelin and tesamorelin are being considered as a treatment option to potentially improve your metabolic support by acting as a synthetic analogue of growth hormone-releasing hormone (GHRH), which stimulates the secretion of growth hormone (GH). Growth hormone plays a critical role in:
• Growth
• Body composition
• Cell repair
• Metabolism

By promoting the natural production of GH, these medications may provide the following benefits:
• Enhance muscle growth
• Improve body composition
• Boost energy levels
• Support overall physical performance

It is commonly used for purposes like weight loss, muscle gain, and anti-aging therapy.

As previously mentioned, the use of sermorelin and tesamorelin to increase growth hormone levels is considered off-label. While legally prescribed, it is not an FDA-approved indication.

Like any medication, these come with potential risks and side effects:

Common side effects:
• Injection site reactions (pain, swelling, redness) if provided as an injection
• Headaches
• Flushing
• Dizziness
• Nausea

Less common but more serious risks:
• Exacerbation of undiagnosed cancers
• Significant changes in blood pressure
• Increased intracranial pressure
• Severe allergic reactions

There are also concerns regarding the long-term use and its potential link to increased cancer risk. While there is no definitive evidence directly linking these to cancer, some studies suggest that prolonged use of growth hormone therapies could potentially increase the risk of certain cancers due to hormonal imbalances. Since these products stimulate the production of growth hormone, it may influence the development of cancerous cells in susceptible individuals.

To ensure safety and effectiveness, regular monitoring is required during treatment including:
• Blood tests every six months to monitor growth hormone levels
• Regular symptom assessments to evaluate the medication's effectiveness
• Follow-up visits to discuss progress, side effects, and necessary adjustments to the treatment plan

You have the right to discuss and consider alternative treatments with your healthcare provider. These alternatives may include:
• Lifestyle modifications (exercise, diet, sleep hygiene, stress management)

As a patient, you are responsible for:
• Attending all scheduled follow-up appointments
• Reporting any side effects or new symptoms to your healthcare provider
• Adhering to the prescribed dosing regimen
• Undergoing all required laboratory tests as part of the monitoring plan if needed

By selecting below, you confirm that you:
• Have read and understood the information provided
• Understand the potential risks and benefits of using sermorelin or tesamorelin, as well as its off-label nature
• Agree to adhere to the monitoring plan and report any concerns or side effects to your healthcare provider`,
  options: [
    { label: 'I have read the consent and understand the content, I wish to proceed', value: 'consent' },
    { label: 'I have read the consent and I do NOT wish to move forward', value: 'decline', disqualifies: true }
  ]
};

const finalVerificationQuestion = {
  id: 'final-verification',
  type: 'consent-dropdown',
  question: 'Patient Verification',
  consentText: `I have reviewed, understand, and attest that the information I have provided above is true to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not in the information that I provided above.

For further medical questions, please reach out to your prescriber. If you're having questions regarding shipping or billing, please reach out to the company.`,
  checkbox: {
    text: 'I accept the Beluga Health Privacy Policy, give my Consent, and agree to the Terms of Service.',
    links: [
      { text: 'Privacy Policy', url: 'https://customerconsents.s3.amazonaws.com/Beluga_Health_PA_Privacy_Policy.pdf' },
      { text: 'Consent', url: 'https://customerconsents.s3.amazonaws.com/Beluga_Health_Telemedicine_Informed_Consent.pdf' },
      { text: 'Terms of Service', url: 'https://fitpal.com/terms-and-conditions/' }
    ]
  },
  options: [
    { label: 'I consent and wish to move forward', value: 'consent' },
    { label: 'I do not wish to continue', value: 'decline', disqualifies: true }
  ]
};

const identityVerificationStep = {
  id: 'identity-verification',
  type: 'file-upload',
  question: 'Identity Verification',
  subtitle: `Upload a photo of a valid government-issued photo ID such as a driver's license or passport which has your picture, name and date of birth clearly visible.

If applicable, upload a photo of the front and the back especially when using a military ID as your date of birth is on the back of the card.

Make sure the photo is clear and legible.`,
  // Required by Beluga — ID photo cannot be skipped (2026-06-27).
};

const shippingAddressStep = {
  id: 'shipping-address',
  type: 'shipping-address',
  question: 'Shipping Address'
};

const checkoutStep = {
  id: 'checkout',
  type: 'checkout',
  question: 'Review treatment and pay'
};

const completeStep = {
  id: 'complete',
  type: 'complete',
  question: 'Complete'
};

// ============================================
// SERMORELIN FLOW CONFIGURATION
// ============================================
export const sermorelinQuestions = {
  sexAtBirthQuestion,
  pregnancyStatusQuestion,
  pregnancyConsentQuestion,
  heightWeightQuestion,
  medicalConditionsQuestion,
  medicationsListQuestion,
  allergiesQuestion,
  healthGoalsQuestion,
  healthGoalsOtherQuestion,
  cardiovascularRatingQuestion,
  strengthRatingQuestion,
  medicalHistoryQuestion,
  thyroidConsentQuestion,
  currentMedicationsQuestion,
  additionalQuestionsQuestion,
  basicContactQuestion,
  mainConsentQuestion,
  finalVerificationQuestion,
  identityVerificationStep,
  shippingAddressStep,
  checkoutStep,
  completeStep
};

// Main flow array
export const sermorelinFlow = [
  sexAtBirthQuestion,
  pregnancyStatusQuestion,
  pregnancyConsentQuestion,
  heightWeightQuestion,
  medicalConditionsQuestion,
  medicationsListQuestion,
  allergiesQuestion,
  healthGoalsQuestion,
  healthGoalsOtherQuestion,
  cardiovascularRatingQuestion,
  strengthRatingQuestion,
  medicalHistoryQuestion,
  thyroidConsentQuestion,
  currentMedicationsQuestion,
  additionalQuestionsQuestion,
  basicContactQuestion,
  mainConsentQuestion,
  finalVerificationQuestion,
  identityVerificationStep,
  shippingAddressStep,
  checkoutStep,
  completeStep
];

// Get question by ID
export const getSermorelinQuestionById = (id) => {
  return sermorelinFlow.find(q => q.id === id);
};

// Build dynamic flow based on answers (handles conditional logic)
export const buildDynamicSermorelinFlow = (answers) => {
  let flow = [...sermorelinFlow];
  
  // Handle conditional questions based on sex
  const sexAtBirth = answers['sex-at-birth'];
  
  if (sexAtBirth === 'male') {
    // Remove pregnancy-related questions for males
    flow = flow.filter(q => !['pregnancy-status', 'pregnancy-consent'].includes(q.id));
  }
  
  // Handle pregnancy consent - only show if female AND pregnancy-status is 'no'
  if (sexAtBirth === 'female') {
    const pregnancyStatus = answers['pregnancy-status'];
    if (pregnancyStatus !== 'no') {
      // Remove pregnancy consent if not answered 'no'
      flow = flow.filter(q => q.id !== 'pregnancy-consent');
    }
  }
  
  // Handle health goals 'other' - only show description if 'other' is selected
  const healthGoals = answers['health-goals'] || [];
  if (!Array.isArray(healthGoals) || !healthGoals.includes('other')) {
    flow = flow.filter(q => q.id !== 'health-goals-other');
  }
  
  // Handle thyroid consent - only show if hypothyroidism is selected
  const medicalHistory = answers['medical-history'] || [];
  if (!Array.isArray(medicalHistory) || !medicalHistory.includes('hypothyroidism')) {
    flow = flow.filter(q => q.id !== 'thyroid-consent');
  }
  
  return flow;
};

// Check if answer is disqualifying
export const isDisqualifyingAnswer = (questionId, answer) => {
  if (questionId === 'pregnancy-status' && answer === 'yes') {
    return {
      disqualified: true,
      reason: 'This treatment is not recommended during pregnancy or while breastfeeding. Please consult with your healthcare provider for alternative options.'
    };
  }
  
  if (questionId === 'medical-history' && Array.isArray(answer)) {
    if (answer.includes('cancer')) {
      return {
        disqualified: true,
        reason: 'Due to your medical history, you may not be a candidate for this treatment. Sermorelin and Tesamorelin are not recommended for patients with active or history of cancer. Please consult with your healthcare provider.'
      };
    }
  }
  
  // Check for disqualifying medications
  if (questionId === 'current-medications' && Array.isArray(answer)) {
    const disqualifyingMeds = ['muscarinic-antagonist', 'somatostatin', 'propylthiouracil'];
    const selectedDisqualifying = answer.filter(med => disqualifyingMeds.includes(med));
    
    if (selectedDisqualifying.length > 0) {
      return {
        disqualified: true,
        reason: 'Based on your current medications, you may not be a candidate for this treatment. The medications you selected may interact with Sermorelin/Tesamorelin therapy. Please consult with your healthcare provider for alternative options.'
      };
    }
  }
  
  const consentQuestions = ['pregnancy-consent', 'thyroid-consent', 'main-consent', 'final-verification'];
  if (consentQuestions.includes(questionId) && answer === 'decline') {
    return {
      disqualified: true,
      reason: 'You have indicated that you do not wish to continue with this treatment.'
    };
  }
  
  return { disqualified: false };
};

// Sermorelin pricing plans (fallback if API unavailable)
export const sermorelinPlans = {
  medicationName: 'Sermorelin',
  brandNames: 'Metabolic Support Therapy',
  plans: [
    {
      plan_slug: 'sermorelin-1-month',
      plan_name: '1-Month Supply',
      plan_price: 199,
      plan_content: 'Sermorelin Injection Kit,Sermorelin 15mg vial,Home injection kit included,Provider consultation,Free shipping,Secure messaging with provider,Easy-to-follow instructions'
    },
    {
      plan_slug: 'sermorelin-3-month',
      plan_name: '3-Month Supply (Best Value)',
      plan_price: 499,
      plan_content: 'Best Value - Save $98,3x Sermorelin 15mg vials,Home injection kit included,Provider consultation,Free shipping,Priority support,Secure messaging with provider,Progress tracking'
    }
  ]
};

export default sermorelinQuestions;
